import React from "react";
import { toast } from "react-toastify";
import { Button, ServerTablePagination } from "../components/ui";
import {
  fetchNotifications,
  getNotificationCampaignName,
  markAllNotificationsRead,
  type NotificationItem,
} from "../utils/notifications";

function getNotificationVisual(n: NotificationItem) {
  const status = String(n.status || n.metadata?.activityStatus || "").toLowerCase();
  const priority = String(n.priority || "info").toLowerCase();

  if (status === "fail" || priority === "critical" || priority === "warning") {
    return {
      symbol: "!",
      iconClass: "bg-proxy-warning-bg text-proxy-warning-icon",
    };
  }
  return {
    symbol: "✦",
    iconClass: "bg-mini-avatar text-mini-avatar-fg",
  };
}

function formatRelativeTime(value?: string | Date | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-2.5 border-t border-thread-line py-[11px] first:border-t-0">
      <div className="h-[27px] w-[27px] shrink-0 rounded-full bg-light-grey" />
      <div className="min-w-0 flex-1">
        <div className="h-3 w-40 rounded bg-light-grey" />
        <div className="mt-2 h-3 w-3/4 rounded bg-light-grey" />
      </div>
      <div className="h-3 w-12 rounded bg-light-grey" />
    </div>
  );
}

export default function NotificationsPage() {
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangeRowsPerPage = (nextSize: number) => {
    setRowsPerPage(nextSize);
    setPage(0);
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchNotifications({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setItems(Array.isArray(res.items) ? res.items : []);
      setTotalCount(Number(res.total || 0));
    } catch (e: any) {
      toast.error(e?.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="noScroll h-full overflow-y-auto overflow-x-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-[22px]">
          <h1 className="text-xl font-semibold text-dark-black">Notifications</h1>
          <p className="mt-1 text-sm text-dark-grey">Updates about your campaigns and activity.</p>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px] text-sm">
          <p className="font-semibold text-dark-blue">● Live updates enabled</p>
          <p className="text-notice-text">
            Campaign and account alerts appear here as they happen across your workspace.
          </p>
        </div>

        <div className="rounded-[10px] border border-line bg-white p-[19px]">
          <div className="mb-[17px] flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[15px] font-semibold tracking-[-0.025em] text-dark-blue">
              Recent notifications
            </h2>
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-[42px] border-line bg-white px-[13px] py-2.5 font-semibold text-dark-blue hover:bg-paper"
              onClick={async () => {
                try {
                  await markAllNotificationsRead();
                  toast.success("Marked all as read");
                  void load();
                } catch (e: any) {
                  toast.error(e?.message || "Failed to mark all read");
                }
              }}
            >
              Mark all as read
            </Button>
          </div>

          {loading ? (
            <>
              <NotificationSkeleton />
              <NotificationSkeleton />
              <NotificationSkeleton />
            </>
          ) : items.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm font-semibold text-dark-black">No notifications yet</p>
              <p className="mt-1 text-xs text-muted">You&apos;re all caught up.</p>
            </div>
          ) : (
            items.map((n) => {
              const style = getNotificationVisual(n);
              const isUnread = !n.readAt;
              const campaignName = getNotificationCampaignName(n);
              const message = [n.message, campaignName ? `Campaign: ${campaignName}` : ""]
                .filter(Boolean)
                .join(" · ");

              return (
                <div
                  key={n._id}
                  className={[
                    "flex cursor-default items-start gap-2.5 border-t border-thread-line py-[11px] first:border-t-0",
                    isUnread ? "bg-thread-active/40 -mx-[19px] px-[19px]" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span
                    className={`grid h-[27px] w-[27px] shrink-0 place-items-center rounded-full text-[11px] font-semibold ${style.iconClass}`}
                    aria-hidden
                  >
                    {style.symbol}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-dark-blue">{n.title}</p>
                    {message ? <p className="mt-0.5 text-[11px] text-muted">{message}</p> : null}
                  </div>

                  <time className="ml-auto shrink-0 whitespace-nowrap text-[10px] text-search-muted">
                    {formatRelativeTime(n.createdAt)}
                  </time>
                </div>
              );
            })
          )}

          {totalCount > 10 ? (
            <div className="mt-4 border-t border-thread-line pt-4">
              <ServerTablePagination
                page={page}
                pageSize={rowsPerPage}
                total={totalCount}
                rowsPerPageOptions={[10, 15, 25, 50, 100]}
                onPageChange={setPage}
                onPageSizeChange={handleChangeRowsPerPage}
                loading={loading}
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
